import { cva, type VariantProps } from 'class-variance-authority';

import { mergeClasses } from '@solvace/zard/shared/utils/merge-classes';

export const buttonVariants = cva(
  mergeClasses(
    'focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40',
    'aria-invalid:border-destructive dark:aria-invalid:border-destructive/50 rounded-lg border border-transparent bg-clip-padding',
    "[font-family:inherit] text-sm font-medium focus-visible:ring-3 aria-invalid:ring-3 [&_svg:not([class*='size-'])]:size-4 inline-flex items-center",
    'justify-center whitespace-nowrap transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none',
    'shrink-0 [&_svg]:shrink-0 outline-none group/button select-none [&_ng-icon]:flex [&_ng-icon]:items-center',
  ),
  {
    variants: {
      zType: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/80',
        destructive:
          'bg-destructive/10 hover:bg-destructive/20 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/20 text-destructive focus-visible:border-destructive/40 dark:hover:bg-destructive/30',
        outline:
          'border-border bg-background hover:bg-muted hover:text-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50 aria-expanded:bg-muted aria-expanded:text-foreground',
        /** Additive, does not replace `outline`. Matches the Solvace UI prototypes' `.btn-outline`
         *  (white bg, primary-colored border/text) — the generic shadcn `outline` above is neutral
         *  gray and does not match that pattern. See RCA Type Registration redesign. */
        'outline-primary':
          'border-primary text-primary bg-background hover:bg-primary/5 aria-expanded:bg-primary/5 disabled:pointer-events-auto disabled:cursor-not-allowed disabled:opacity-[.45]',
        /** Additive, does not replace `default`. Matches the Solvace UI prototypes' `.btn-primary`: solid primary,
         *  hover on the darker `--primary-hover` (the tenant palette's `primary-700`), and the prototypes' shared
         *  `.btn[disabled]{opacity:.45;cursor:not-allowed}` — Zard's base `disabled:pointer-events-none` would hide
         *  that cursor. See Action Plan Systems Registration (AB#72295). */
        'solid-primary':
          'bg-primary text-primary-foreground hover:bg-primary-700 disabled:pointer-events-auto disabled:cursor-not-allowed disabled:opacity-[.45]',
        /** Additive, does not replace `destructive` (a pale/subtle style). Matches the Solvace UI
         *  prototypes' `.btn-destructive` (solid red bg, white text) — used for destructive confirm
         *  actions like "Delete". See RCA Type Registration redesign. */
        'solid-destructive': 'bg-destructive text-white hover:bg-destructive/90',
        /** Additive, does not replace `solid-destructive`. Matches the Solvace UI prototypes' `.btn-destructive` exactly:
         *  solid `#EC221F` (`bg-destructive` through the module token bridge), white text, hover on the prototypes' fixed
         *  darker `#d41d1a` (not a tenant token — the same literal in every prototype) and the shared
         *  `.btn[disabled]{opacity:.45;cursor:not-allowed}`. Used by the design-system confirm dialog (AB#72295). */
        warning: 'bg-destructive text-white hover:bg-[#d41d1a] disabled:pointer-events-auto disabled:cursor-not-allowed disabled:opacity-[.45]',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80 aria-expanded:bg-secondary aria-expanded:text-secondary-foreground',
        ghost:
          'hover:bg-muted hover:text-foreground dark:hover:bg-muted/50 aria-expanded:bg-muted aria-expanded:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      zSize: {
        default: 'h-8 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
        xs: "h-6 gap-1 rounded-[min(var(--radius-md),10px)] px-2 text-xs in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3",
        sm: "h-7 gap-1 rounded-[min(var(--radius-md),12px)] px-2.5 text-[0.8rem] in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 [&_svg:not([class*='size-'])]:size-3.5",
        lg: 'h-9 gap-1.5 px-2.5 has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3',
        icon: 'size-8',
        'icon-xs':
          "size-6 rounded-[min(var(--radius-md),10px)] in-data-[slot=button-group]:rounded-lg [&_svg:not([class*='size-'])]:size-3",
        'icon-sm': 'size-7 rounded-[min(var(--radius-md),12px)] in-data-[slot=button-group]:rounded-lg',
        'icon-lg': 'size-9',
        /** Additive. The Solvace prototypes collapse a labelled 40px toolbar button to a 40px icon-only square on
         *  phones (`.perm-toolbar .btn{width:40px;padding:0}`); no upstream icon size is 40px. */
        'icon-xl': 'size-10',
        /** Additive. Solvace design-system button: 40px tall, 16px side padding, 8px gap, weight 600
         *  (design-system/index.html "Sizing & shape" and `.btn`). Replaces the per-app `!important` restyles. */
        ds: 'h-10 gap-2 px-4 text-sm font-semibold has-data-[icon=inline-end]:pr-3 has-data-[icon=inline-start]:pl-3',
        /** Additive. Solvace design-system `.btn-sm`: 34px tall, 12px side padding, 13px text. */
        'ds-sm': 'h-[34px] gap-1.5 px-3 text-[13px] font-semibold has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2',
      },
      zShape: {
        default: 'rounded-md',
        circle: 'rounded-full',
        square: 'rounded-none',
      },
      zFull: {
        true: 'w-full',
      },
      zLoading: {
        true: 'pointer-events-none opacity-50',
      },
      zDisabled: {
        true: 'pointer-events-none opacity-50',
      },
    },
    defaultVariants: {
      zType: 'default',
      zSize: 'default',
      zShape: 'default',
    },
  },
);
export type ZardButtonShapeVariants = NonNullable<VariantProps<typeof buttonVariants>['zShape']>;
export type ZardButtonSizeVariants = NonNullable<VariantProps<typeof buttonVariants>['zSize']>;
export type ZardButtonTypeVariants = NonNullable<VariantProps<typeof buttonVariants>['zType']>;
